/**
 * The onboarding wizard's rail — connected segments rather than separate chips.
 *
 * **It keeps the connected-segment shape the audit's step bar gave up**, and the difference
 * is the one `audit/steps.ts` draws: a wizard is one actor filling one form, so it has a
 * high-water mark and filling it in is exactly what the reader is doing. An audit has three
 * parties and a state machine, no high-water mark, and stops with different owners — so its
 * bar became six things chosen between. A shared `.step` class made the two look alike;
 * `.steprail` is this one's own.
 *
 * A finished step is Green Wash, the current one Deep Forest with an amber number. A step
 * already visited can be returned to freely; a step ahead can only be reached once the
 * current one has nothing blocking.
 *
 * Generic over what a step *is*, because two flows have one rail: the certification body's
 * seven server answers over six segments, and the auditor's six over six. Whether a segment
 * is done is the caller's question — it is the server's verdict, and the two flows ask it of
 * different validators.
 */
export function StepRail({
  titles,
  current,
  furthest,
  isComplete,
  onSelect,
}: {
  titles: readonly string[];
  current: number;
  furthest: number;
  isComplete: (index: number) => boolean;
  onSelect: (index: number) => void;
}) {
  return (
    <div className="steprail">
      {titles.map((title, index) => {
        const state = index === current ? "now" : isComplete(index) ? "done" : "";

        return (
          <button
            type="button"
            key={title}
            className={state}
            disabled={index > furthest}
            aria-current={index === current ? "step" : undefined}
            onClick={() => onSelect(index)}
          >
            <b>Step {index + 1}</b>
            {title}
          </button>
        );
      })}
    </div>
  );
}
